import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { RequestType } from '@strata/react-chat';
import { type GridWithFilters } from '@strata/react-grid';
import { type ComponentProps } from 'react';

export const Grids = {
    ...RequestType,
} as const;
export type Grid = (typeof Grids)[keyof typeof Grids];

export interface GridState {
    sort?: ComponentProps<typeof GridWithFilters>['sortProperties'];
    search?: string;
    activeFilters?: ComponentProps<typeof GridWithFilters>['activeFilters'];
    filterPaneWidth?: number;
    page: number;
}

const initialGridState: GridState = { page: 0 };

const initGridStates = () =>
    Object.fromEntries(Object.values(Grids).map((grid) => [grid, initialGridState])) as Record<
        Grid,
        GridState
    >;

export const gridsSlice = createSlice({
    name: 'grids',
    initialState: initGridStates(),
    reducers: {
        setGridSort(state, action: PayloadAction<Pick<GridState, 'sort'> & { grid: Grid }>) {
            const { grid, sort } = action.payload;
            return { ...state, [grid]: { ...state[grid], sort } };
        },
        setGridSearch(state, action: PayloadAction<Pick<GridState, 'search'> & { grid: Grid }>) {
            const { grid, search } = action.payload;
            return { ...state, [grid]: { ...state[grid], search } };
        },
        setGridActiveFilters(
            state,
            action: PayloadAction<Pick<GridState, 'activeFilters'> & { grid: Grid }>,
        ) {
            const { grid, activeFilters } = action.payload;
            return { ...state, [grid]: { ...state[grid], activeFilters } };
        },
        setFilterPaneWidth(
            state,
            action: PayloadAction<Pick<GridState, 'filterPaneWidth'> & { grid: Grid }>,
        ) {
            const { grid, filterPaneWidth } = action.payload;
            return { ...state, [grid]: { ...state[grid], filterPaneWidth } };
        },
        setCurrentPage(state, action: PayloadAction<Pick<GridState, 'page'> & { grid: Grid }>) {
            const { grid, page } = action.payload;
            return { ...state, [grid]: { ...state[grid], page } };
        },
        resetGrid(state, action: PayloadAction<Grid>) {
            const grid = action.payload;
            return { ...state, [grid]: initialGridState };
        },
    },
});

export const {
    setGridSort,
    setGridSearch,
    setGridActiveFilters,
    setFilterPaneWidth,
    setCurrentPage,
    resetGrid,
} = gridsSlice.actions;
